import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../services/api";
import "./CaregiverSubscription.css";

const DISMISS_KEY = "nema_cg_sub_gate_dismissed";

/**
 * Dashboard prompt when a caregiver cannot receive bookings.
 * Inactive plan uses this popup. Zero credits uses the same popup with credit copy.
 */
export default function CaregiverSubscriptionGate() {
  const navigate = useNavigate();
  const location = useLocation();
  const [overview, setOverview] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const path = location.pathname;
    const onSetup =
      path.startsWith("/caregiver/subscription") ||
      path.startsWith("/caregiver/payments") ||
      path.startsWith("/caregiver/complete-profile") ||
      path.startsWith("/caregiver/register") ||
      path.startsWith("/caregiver/compliance-onboarding") ||
      path.startsWith("/caregiver/approval-status");
    if (onSetup) {
      setOpen(false);
      return undefined;
    }
    let cancelled = false;
    async function load() {
      try {
        const data = await api.getCaregiverHubSubscription();
        if (cancelled) return;
        setOverview(data || null);
        const inactive = !data?.subscription?.inForce;
        const out = Boolean(data?.outOfCredits);
        const reason = inactive ? "inactive" : out ? "credits" : "";
        const dismissed = window.sessionStorage.getItem(DISMISS_KEY);
        setOpen(Boolean(reason) && dismissed !== reason);
      } catch {
        if (!cancelled) setOpen(false);
      }
    }
    load();
    window.addEventListener("nema:caregiver-subscription-updated", load);
    return () => {
      cancelled = true;
      window.removeEventListener("nema:caregiver-subscription-updated", load);
    };
  }, [location.pathname]);

  if (!open || !overview) return null;

  const inactive = !overview.subscription?.inForce;
  const available = overview.credits?.availableCredits ?? 0;

  function dismiss() {
    window.sessionStorage.setItem(DISMISS_KEY, inactive ? "inactive" : "credits");
    setOpen(false);
  }

  function goSubscribe() {
    dismiss();
    navigate("/caregiver/subscription");
  }

  return (
    <div className="cg-sub-modal" role="dialog" aria-modal="true" aria-labelledby="cg-sub-gate-title">
      <div className="cg-sub-modal__card">
        <button type="button" className="cg-sub-modal__close" aria-label="Close" onClick={dismiss}>
          ×
        </button>
        <div className="cg-sub-modal__hero">
          <div className="cg-sub-modal__bubble" aria-hidden="true">
            <span>{inactive ? "!" : available}</span>
            <span>{inactive ? "Plan" : "Credits"}</span>
          </div>
        </div>
        <h2 id="cg-sub-gate-title">
          {inactive ? "You don't have an active subscription plan" : "You're out of booking credits!"}
        </h2>
        <p className="cg-sub-modal__lead">
          {inactive
            ? "Without an active plan, you will not receive any booking requests."
            : `You currently have ${available} credits. You will not receive any new booking requests until you add more credits.`}
        </p>
        <div className="cg-sub-modal__note">
          {inactive
            ? "Activate a plan to start receiving bookings. Care Attendant subscription fees are waived until 31 March 2027. Nurses and physiotherapists get the first 30 days free."
            : "Each successful booking uses 1 credit. You get 2 free credits every month with your subscription plan."}
        </div>
        <button type="button" className="cg-sub__btn cg-sub__btn--primary" onClick={goSubscribe}>
          {inactive
            ? "Activate plan"
            : `Buy more credits (${overview.creditPrice ? `₹${overview.creditPrice}` : ""} each, incl. 18% GST)`}
        </button>
        <button type="button" className="cg-sub__btn" onClick={goSubscribe}>
          Go to Subscription &amp; Credits
        </button>
      </div>
    </div>
  );
}
